import { Section } from "../../components/Section/Section";
import { FadeInUp } from "../../components/Animation/ScrollAnimation";
import { EventsSliderOptionsType } from "@/types/ModuleOptionsType";
import styles from "./EventSlider.module.scss";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNavigation,
} from "@aleksundshantu/aunds-utils-react/primitives/carousel";
import {
  EndCard,
  EventCard,
  EventCardType,
} from "../../components/EventCard/EventCard";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { useSublinesVisibility } from "../../hooks/useSublinesOverlinesVisibility";
import { Modul_Termin_Slider } from "../../wp/types/acf-module-types";
import { EndCardsType } from "@/types/OptionType";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { columnSize } from "@/utils/layout/columnSize";
import { EventSliderData } from "@/getData/getIndexedEventSliderData";
import { Headline } from "@/components/Headline/Headline";

export type EventsSliderType = Modul_Termin_Slider;

interface EventsSliderProps {
  data: EventsSliderType;
  eventsSliderOptions: EventsSliderOptionsType;
  eventSliderData: EventSliderData;
  endCardData: EndCardsType;
}

export type EventsDataType = {
  totalPosts: number;
  elements: EventCardType[];
};
{
  /* jscpd:ignore-start */
}
const EventSliderModule = ({
  data,
  eventsSliderOptions,
  eventSliderData,
  endCardData,
}: EventsSliderProps) => {
  const { headline_html_tag, headline, overline, subline } = data.events_slider;
  const {
    section_margin_class,
    section_padding_class,
    background_color_class,
    module_width,
  } = data.layout;

  const {
    box: { show_border_radius, show_box_shadow, show_card_box },
    show_overline_subline,
    slider_options: {
      cards_slider_options: { end_card, show_end_card, show_old_events },
      slider_navigation,
      has_autoplay,
    },
    columns,
  } = eventsSliderOptions;

  const { events, moreUpcomingEvents } = eventSliderData;
  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline,
  );

  const closestIndex = calcClosestIndex(events); // +1 because <EndCard/> is inserted at the beginning

  const ITEM_WIDTH = {
    mobile: "100%",
    tablet: "50%",
    desktop: "34%",
  };

  const { getLabel } = useGlobalContext();
  const cardLabel = getLabel("aria_label_events_slider_card");

  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }

  return (
    <Section
      className={`event-slider ${background_color_class} ${section_margin_class} ${section_padding_class}`}
      frameless={module_width === "frameless"}
      columnsClassName="is-centered is-multiline"
    >
      <div
        className={`column ${module_width === "narrow-frame" ? columnSize(columns) : "is-12"}`}
      >
        <FadeInUp>
          <Headline
            headline_html_tag={headline_html_tag}
            headline={headline}
            overline={showOverline ? overline : ""}
            subline={showSubline ? subline : ""}
            extraClass="has-text-centered"
            wrapInSection={module_width === "frameless"}
            disableAnimation
          />
        </FadeInUp>
        <FadeInUp>
          <Carousel align={"start"} autoplay={has_autoplay}>
            <CarouselContent
              outerPadding="16px"
              overflowVisible={module_width === "frameless"}
              spacing="2rem"
              jumpToIndex={closestIndex}
              extraClasses={module_width === "normal-frame" ? "is-clipped" : ""}
            >
              {show_old_events && show_end_card && endCardData.link && (
                <CarouselItem itemWidth={ITEM_WIDTH}>
                  <EndCard
                    headlineTag={headline_html_tag}
                    end_card={{
                      background_color: end_card.background_color,
                      button_type: end_card.button_type,
                      link: endCardData.link,
                      title: endCardData.title,
                    }}
                    show_border_radius={show_border_radius}
                    show_box_shadow={show_box_shadow}
                    showBox={show_card_box}
                  />
                </CarouselItem>
              )}
              {events.map((element, index) => {
                const ariaLabel = cardLabel
                  .replace('"${i}"', `${index + 1}`)
                  .replace('"${total}"', `${events.length}`)
                  .replace(/"/g, "");

                return (
                  <CarouselItem
                    itemWidth={ITEM_WIDTH}
                    key={index}
                    aria-label={ariaLabel}
                    className={styles["event-card"]}
                  >
                    <EventCard
                      key={index}
                      element={element}
                      show_border_radius={show_border_radius}
                      show_box_shadow={show_box_shadow}
                      moduleType="events-slider"
                      showBox={show_card_box}
                      headlineTag={headline_html_tag}
                    />
                  </CarouselItem>
                );
              })}
              {moreUpcomingEvents && show_end_card && (
                <CarouselItem itemWidth={ITEM_WIDTH}>
                  <EndCard
                    headlineTag={headline_html_tag}
                    end_card={{
                      background_color: end_card.background_color,
                      button_type: end_card.button_type,
                      link: endCardData.link,
                      title: endCardData.title,
                    }}
                    show_border_radius={show_border_radius}
                    show_box_shadow={show_box_shadow}
                    showBox={show_card_box}
                  />
                </CarouselItem>
              )}
            </CarouselContent>
            <CarouselNavigation type={slider_navigation} />
          </Carousel>
        </FadeInUp>
      </div>
    </Section>
  );
};
{
  /* jscpd:ignore-end */
}
export default EventSliderModule;

const calcClosestIndex = (events: EventCardType[]): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Remove time portion for accurate date comparison
  let closestIndex = -1;

  for (let i = 0; i < events.length; i++) {
    const eventDate = events[i].start_date.split("-");
    const eventDateObj = new Date(
      parseInt(eventDate[2]),
      parseInt(eventDate[1]) - 1,
      parseInt(eventDate[0]),
    );

    if (eventDateObj >= today) {
      closestIndex = i;
      break; // Stop once we find the first event happening today or later
    }
  }
  return closestIndex;
};
